import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { userApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import LocationSelect from '@/components/forms/LocationSelect'
import { validateEmail, validatePhone } from '@/utils/validation'
import { User, Mail, Phone, MapPin, FileText, Eye, DollarSign } from 'lucide-react'

export default function Profile() {
  const { user, updateUser } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    profession: '',
    continent: '',
    country: '',
    state: '',
    city: '',
    business_name: '',
    business_description: '',
  })

  const [withdrawalData, setWithdrawalData] = useState({
    withdrawal_type: '',
    zelle_email: '',
    paypal_email: '',
    ach_bank_name: '',
    ach_account_holder: '',
    ach_account_number: '',
    ach_routing_number: '',
    ach_account_type: '',
  })

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || user.phone_number || '',
        profession: user.profession || '',
        continent: user.continent || '',
        country: user.country || '',
        state: user.state || '',
        city: user.city || '',
        business_name: user.business_name || '',
        business_description: user.business_description || '',
      })
      setWithdrawalData({
        withdrawal_type: user.withdrawal_type || '',
        zelle_email: user.zelle_email || '',
        paypal_email: user.paypal_email || '',
        ach_bank_name: user.ach_bank_name || '',
        ach_account_holder: user.ach_account_holder || '',
        ach_account_number: user.ach_account_number || '',
        ach_routing_number: user.ach_routing_number || '',
        ach_account_type: user.ach_account_type || '',
      })
    }
  }, [user])

  const updateProfileMutation = useMutation({
    mutationFn: (data: any) => userApi.updateProfile(data),
    onSuccess: (response) => {
      if (response.data?.profile) {
        updateUser(response.data.profile)
      }
      queryClient.invalidateQueries({ queryKey: ['user'] })
      setSuccess('Profile updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update profile')
    },
  })

  const updatePasswordMutation = useMutation({
    mutationFn: (data: any) => userApi.updatePassword(data),
    onSuccess: () => {
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' })
      setSuccess('Password updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update password')
    },
  })

  const updateWithdrawalMutation = useMutation({
    mutationFn: (data: any) => userApi.updateProfile(data),
    onSuccess: (response) => {
      if (response.data?.profile) {
        updateUser(response.data.profile)
        // Update local withdrawal data state
        setWithdrawalData({
          withdrawal_type: response.data.profile.withdrawal_type || '',
          zelle_email: response.data.profile.zelle_email || '',
          paypal_email: response.data.profile.paypal_email || '',
          ach_bank_name: response.data.profile.ach_bank_name || '',
          ach_account_holder: response.data.profile.ach_account_holder || '',
          ach_account_number: response.data.profile.ach_account_number || '',
          ach_routing_number: response.data.profile.ach_routing_number || '',
          ach_account_type: response.data.profile.ach_account_type || '',
        })
      }
      queryClient.invalidateQueries({ queryKey: ['user'] })
      setSuccess('Withdrawal settings updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update withdrawal settings')
    },
  })

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!validateEmail(formData.email)) {
      setError('Invalid email format')
      return
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      setError('Invalid phone number format')
      return
    }

    // Transform phone to phone_number for backend
    const { phone, ...rest } = formData
    const submitData = {
      ...rest,
      phone_number: phone,
    }

    updateProfileMutation.mutate(submitData)
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Passwords do not match')
      return
    }

    updatePasswordMutation.mutate({
      current_password: passwordData.current_password,
      new_password: passwordData.new_password,
    })
  }

  const handleWithdrawalSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate based on withdrawal type
    if (withdrawalData.withdrawal_type === 'zelle' && !withdrawalData.zelle_email) {
      setError('Zelle email is required')
      return
    }

    if (withdrawalData.withdrawal_type === 'paypal' && !withdrawalData.paypal_email) {
      setError('PayPal email is required')
      return
    }

    if (withdrawalData.withdrawal_type === 'ach') {
      if (!withdrawalData.ach_bank_name || !withdrawalData.ach_account_holder ||
          !withdrawalData.ach_account_number || !withdrawalData.ach_routing_number ||
          !withdrawalData.ach_account_type) {
        setError('All ACH bank details are required')
        return
      }
    }

    updateWithdrawalMutation.mutate(withdrawalData)
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">My Profile</h1>
        <Button variant="secondary" onClick={() => navigate(`/dashboard/users/${user?.id}`)}>
          <Eye className="w-4 h-4 mr-2" />
          View My Profile
        </Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Profile Information */}
      <form onSubmit={handleProfileSubmit}>
        <Card title="Profile Information">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  <User className="w-4 h-4 inline mr-2" />
                  First Name *
                </label>
                <input
                  type="text"
                  className="input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="label">
                  <User className="w-4 h-4 inline mr-2" />
                  Last Name *
                </label>
                <input
                  type="text"
                  className="input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email *
                </label>
                <input
                  type="email"
                  className="input bg-gray-100 cursor-not-allowed"
                  value={formData.email}
                  readOnly
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email address cannot be changed. Contact support if you need to update it.
                </p>
              </div>

              <div>
                <label className="label">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone
                </label>
                <input
                  type="tel"
                  className="input"
                  placeholder="+1234567890"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label">
                <FileText className="w-4 h-4 inline mr-2" />
                Profession
              </label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Web Developer, Graphic Designer"
                value={formData.profession}
                onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
              />
            </div>

            <div>
              <label className="label mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Location
              </label>
              <LocationSelect
                continent={formData.continent}
                country={formData.country}
                state={formData.state}
                city={formData.city}
                onContinentChange={(continent) =>
                  setFormData({ ...formData, continent, country: '', state: '', city: '' })
                }
                onCountryChange={(country) =>
                  setFormData({ ...formData, country, state: '', city: '' })
                }
                onStateChange={(state) =>
                  setFormData({ ...formData, state, city: '' })
                }
                onCityChange={(city) => setFormData({ ...formData, city })}
              />
            </div>

            {user?.user_type === 'professional' && (
              <>
                <div>
                  <label className="label">
                    <FileText className="w-4 h-4 inline mr-2" />
                    Business Name
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={formData.business_name}
                    onChange={(e) =>
                      setFormData({ ...formData, business_name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="label">
                    <FileText className="w-4 h-4 inline mr-2" />
                    Business Description
                  </label>
                  <textarea
                    className="textarea"
                    rows={4}
                    value={formData.business_description}
                    onChange={(e) =>
                      setFormData({ ...formData, business_description: e.target.value })
                    }
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <Button type="submit" isLoading={updateProfileMutation.isPending}>
              Update Profile
            </Button>
          </div>
        </Card>
      </form>

      {/* Change Password */}
      <form onSubmit={handlePasswordSubmit}>
        <Card title="Change Password">
          <div className="space-y-4">
            <div>
              <label className="label">Current Password *</label>
              <input
                type="password"
                className="input"
                value={passwordData.current_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, current_password: e.target.value })
                }
                required
              />
            </div>

            <div>
              <label className="label">New Password *</label>
              <input
                type="password"
                className="input"
                value={passwordData.new_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, new_password: e.target.value })
                }
                required
                minLength={8}
              />
              <p className="text-sm text-gray-500 mt-1">
                Must be at least 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            <div>
              <label className="label">Confirm New Password *</label>
              <input
                type="password"
                className="input"
                value={passwordData.confirm_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirm_password: e.target.value })
                }
                required
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              type="submit"
              variant="secondary"
              isLoading={updatePasswordMutation.isPending}
            >
              Change Password
            </Button>
          </div>
        </Card>
      </form>

      {/* Withdrawal Settings */}
      <form onSubmit={handleWithdrawalSubmit}>
        <Card title="Withdrawal Settings">
          <p className="text-sm text-gray-600 mb-4">
            <DollarSign className="w-4 h-4 inline mr-2" />
            Configure your preferred withdrawal method for cashing out Barter Coins
          </p>

          <div className="space-y-6">
            <div>
              <label className="label">Withdrawal Type *</label>
              <select
                className="input"
                value={withdrawalData.withdrawal_type}
                onChange={(e) => setWithdrawalData({ ...withdrawalData, withdrawal_type: e.target.value })}
              >
                <option value="">Select withdrawal method</option>
                <option value="zelle">Zelle</option>
                <option value="paypal">PayPal</option>
                <option value="ach">ACH Bank Transfer</option>
              </select>
            </div>

            {withdrawalData.withdrawal_type === 'zelle' && (
              <div>
                <label className="label">Zelle Email *</label>
                <input
                  type="email"
                  className="input"
                  placeholder="your-email@example.com"
                  value={withdrawalData.zelle_email}
                  onChange={(e) => setWithdrawalData({ ...withdrawalData, zelle_email: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is the email address registered with your Zelle account
                </p>
              </div>
            )}

            {withdrawalData.withdrawal_type === 'paypal' && (
              <div>
                <label className="label">PayPal Email *</label>
                <input
                  type="email"
                  className="input"
                  placeholder="your-paypal@example.com"
                  value={withdrawalData.paypal_email}
                  onChange={(e) => setWithdrawalData({ ...withdrawalData, paypal_email: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is the email address registered with your PayPal account
                </p>
              </div>
            )}

            {withdrawalData.withdrawal_type === 'ach' && (
              <>
                <div>
                  <label className="label">Bank Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Bank of America"
                    value={withdrawalData.ach_bank_name}
                    onChange={(e) => setWithdrawalData({ ...withdrawalData, ach_bank_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="label">Account Holder Name *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="John Doe"
                    value={withdrawalData.ach_account_holder}
                    onChange={(e) => setWithdrawalData({ ...withdrawalData, ach_account_holder: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Routing Number *</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="123456789"
                      value={withdrawalData.ach_routing_number}
                      onChange={(e) => setWithdrawalData({ ...withdrawalData, ach_routing_number: e.target.value })}
                      required
                      maxLength={9}
                    />
                  </div>

                  <div>
                    <label className="label">Account Number *</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="000123456789"
                      value={withdrawalData.ach_account_number}
                      onChange={(e) => setWithdrawalData({ ...withdrawalData, ach_account_number: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Account Type *</label>
                  <select
                    className="input"
                    value={withdrawalData.ach_account_type}
                    onChange={(e) => setWithdrawalData({ ...withdrawalData, ach_account_type: e.target.value })}
                    required
                  >
                    <option value="">Select account type</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {withdrawalData.withdrawal_type && (
            <div className="mt-6 flex justify-end">
              <Button type="submit" isLoading={updateWithdrawalMutation.isPending}>
                Save Withdrawal Settings
              </Button>
            </div>
          )}
        </Card>
      </form>

      {/* Account Information */}
      <Card title="Account Information">
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Account Type:</span>
            <span className="font-semibold capitalize">{user?.user_type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Member Since:</span>
            <span className="font-semibold">
              {user?.created_at
                ? (() => {
                    const d = new Date(user.created_at)
                    const mm = String(d.getMonth() + 1).padStart(2, '0')
                    const dd = String(d.getDate()).padStart(2, '0')
                    return `${mm}/${dd}/${d.getFullYear()}`
                  })()
                : '-'}
            </span>
          </div>
          {user?.user_type === 'professional' && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Subscription Status:</span>
                <span
                  className={`font-semibold ${
                    user.subscription_status === 'active' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {user.subscription_status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
              {user.subscription_tier === 'paid' && user.subscription_status === 'active' && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tier:</span>
                  <span className="font-semibold text-yellow-600">Pro Member</span>
                </div>
              )}
            </>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Barter Coin Balance:</span>
            <span className="font-semibold">{user?.barter_coin_balance || 0} coins</span>
          </div>
        </div>
      </Card>
    </div>
  )
}
